// src/components/dashboard/BoletoCheckoutAsaas.jsx
import React, { useState } from 'react';
import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    Flex,
    Input,
    Link,
    useToast,
} from '@chakra-ui/react';
import { FiFileText, FiCopy, FiExternalLink, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { supabase } from '../../config/supabaseClient';

const BoletoCheckoutAsaas = ({ userId, selectedPackage, validCoupon, couponCode, packages, onSuccess, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [boletoUrl, setBoletoUrl] = useState('');
    const [boletoCode, setBoletoCode] = useState('');
    const [paymentId, setPaymentId] = useState('');
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState(null); // Para debug
    const toast = useToast();

    const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);

    // Calcular preço com desconto
    const calculatePrice = () => {
        if (!selectedPkg) return 0;
        const basePrice = selectedPkg.price;
        return validCoupon ? basePrice * 0.85 : basePrice; // 15% de desconto
    };

    const handleGenerateBoleto = async () => {
        setLoading(true);
        setError('');
        setDebugInfo(null);

        try {
            const { data: { session } } = await supabase.auth.getSession();

            if (!session) {
                throw new Error('Usuário não está autenticado. Faça login novamente.');
            }

            const authToken = session.access_token;

            // Registrar para debug
            setDebugInfo(prev => ({
                ...prev,
                auth: `Autenticado: ${!!authToken}`,
                token_preview: authToken ? `${authToken.substring(0, 10)}...` : 'Nenhum token'
            }));

            // Verificar CPF/CNPJ no perfil
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('cpf_cnpj, name, phone, email')
                .eq('id', userId)
                .single();

            if (profileError) {
                throw new Error(`Erro ao verificar perfil: ${profileError.message}`);
            }

            if (!profileData.cpf_cnpj) {
                throw new Error('É necessário cadastrar seu CPF/CNPJ no perfil para realizar pagamentos');
            }

            // Registrar para debug
            setDebugInfo(prev => ({
                ...prev,
                profile_data: JSON.stringify(profileData, null, 2)
            }));

            // Limpar o CPF/CNPJ (remover pontuações)
            const cleanCpfCnpj = profileData.cpf_cnpj.replace(/\D/g, '');

            // Chamar a Edge Function para gerar o boleto
            const response = await fetch('https://mlliabdepyirxtaytcco.functions.supabase.co/create-asaas-boleto', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    amount: calculatePrice(),
                    currency: 'brl',
                    package_id: selectedPackage,
                    credits: selectedPkg.credits,
                    coupon_code: validCoupon ? couponCode : null,
                    coupon_discount: validCoupon ? 15 : 0,
                    customer: {
                        name: profileData.name || 'Cliente',
                        email: profileData.email,
                        phone: profileData.phone,
                        cpfCnpj: cleanCpfCnpj
                    }
                }),
            });

            // Registrar resposta para debug
            setDebugInfo(prev => ({
                ...prev,
                response_status: response.status,
                response_ok: response.ok
            }));

            const responseData = await response.json();

            setDebugInfo(prev => ({
                ...prev,
                response_data: JSON.stringify(responseData, null, 2)
            }));

            if (responseData.error) {
                throw new Error(responseData.error);
            }

            // Atualizar o estado com os dados do boleto
            setBoletoUrl(responseData.boletoUrl);
            setBoletoCode(responseData.boletoCode);
            setPaymentId(responseData.paymentId);

        } catch (error) {
            console.error('Erro ao gerar boleto:', error);
            setError(error.message || 'Ocorreu um erro ao gerar o boleto.');

            toast({
                title: 'Erro ao gerar boleto',
                description: error.message || 'Ocorreu um erro ao gerar o boleto.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCopyBoletoCode = () => {
        navigator.clipboard.writeText(boletoCode);
        setCopied(true);

        toast({
            title: 'Código copiado!',
            description: 'O código do boleto foi copiado para sua área de transferência.',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });

        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <VStack spacing={6}>
            {/* Resumo da compra */}
            <Box p={4} bg="gray.50" borderRadius="md" width="100%">
                <Heading size="md" mb={2} color="gray.700">
                    Resumo
                </Heading>

                <HStack justify="space-between" mb={2}>
                    <Text>Pacote:</Text>
                    <Text fontWeight="medium">{selectedPkg?.description}</Text>
                </HStack>

                <HStack justify="space-between" mb={2}>
                    <Text>Valor original:</Text>
                    <Text>R$ {selectedPkg?.price.toFixed(2)}</Text>
                </HStack>

                {validCoupon && (
                    <HStack justify="space-between" mb={2}>
                        <Text>Desconto (15%):</Text>
                        <Text color="green.500">-R$ {(selectedPkg?.price * 0.15).toFixed(2)}</Text>
                    </HStack>
                )}

                <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
                    <Text fontWeight="bold">Total:</Text>
                    <Text fontWeight="bold" color="brand.primary" fontSize="xl">
                        R$ {calculatePrice().toFixed(2)}
                    </Text>
                </HStack>
            </Box>

            {/* Informações do Boleto */}
            <Box p={4} borderWidth="1px" borderRadius="md" width="100%">
                <VStack spacing={4} align="stretch">
                    <Heading size="sm" mb={2}>
                        Pagamento via Boleto
                    </Heading>

                    {!boletoUrl ? (
                        <Button
                            onClick={handleGenerateBoleto}
                            colorScheme="blue"
                            isLoading={loading}
                            leftIcon={<FiFileText />}
                            size="lg"
                            width="full"
                        >
                            Gerar Boleto
                        </Button>
                    ) : (
                        <VStack spacing={4} align="center">
                            <Text fontSize="sm" textAlign="center">
                                O boleto foi gerado com sucesso. Você pode imprimi-lo ou copiar o código de barras.
                                O pagamento será confirmado em até 3 dias úteis após o pagamento.
                            </Text>

                            <Button
                                as={Link}
                                href={boletoUrl}
                                isExternal
                                colorScheme="blue"
                                width="full"
                                leftIcon={<FiExternalLink />}
                            >
                                Abrir Boleto
                            </Button>

                            <Box p={3} bg="blue.50" borderRadius="md" width="100%">
                                <Text fontWeight="medium" mb={2}>
                                    Código de Barras:
                                </Text>
                                <Flex>
                                    <Input
                                        value={boletoCode}
                                        isReadOnly
                                        bg="white"
                                        borderRightRadius={0}
                                    />
                                    <Button
                                        onClick={handleCopyBoletoCode}
                                        colorScheme="blue"
                                        borderLeftRadius={0}
                                        leftIcon={copied ? <FiCheckCircle /> : <FiCopy />}
                                    >
                                        {copied ? "Copiado" : "Copiar"}
                                    </Button>
                                </Flex>
                            </Box>

                            <Text fontSize="sm" color="gray.600">
                                Após o pagamento, seus créditos serão adicionados automaticamente à sua conta.
                            </Text>
                        </VStack>
                    )}

                    {error && (
                        <Box p={3} bg="red.50" borderRadius="md" width="100%">
                            <HStack>
                                <FiAlertCircle color="red" />
                                <Text color="red.500" fontSize="sm">
                                    {error}
                                </Text>
                            </HStack>
                        </Box>
                    )}
                </VStack>
            </Box>
        </VStack>
    );
};

export default BoletoCheckoutAsaas;